import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import StepperControls from "@components/StepperControls/StepperControls";
import StepperTabPanel from "@components/StepperTabs/StepperTabPanel";
import StepperTabs from "@components/StepperTabs/StepperTabs";
import {
  STEPPER_TYPE,
  StepStatus,
  type StepperTabsHandle,
  type StepStatusValue,
} from "@components/StepperTabs/StepperTabs.types";
import { useStepStatuses } from "@components/StepperTabs/useStepStatuses";
import { useNotification } from "@context/NotificationContext";
import { nominationDocumentDateKeys } from "@features/vessel-nomination/utils";
import { usePersistentForm } from "@hooks/usePersistentForm";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { VesselNominationDetailsResponse } from "@services/vessel-nomination/types";
import type { Vessel } from "@services/vessel/types";
import { getVesselByIdNoCompanyIdValidation } from "@services/vesselsApi";
import { useQuery } from "@tanstack/react-query";
import { hasFormData } from "@utils/form";
import { paths } from "@utils/navigation";
import { checkPermission } from "@utils/user-permissions";
import type { FieldValues, SubmitErrorHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { FORM_FIELDS, STEP_FIELDS, steps, type VesselNominationOutletContext } from "../constants";
import { useUploadDocument } from "../hooks/useUploadDocument";
import { useVesselNomination } from "../hooks/useVesselNomination";
import {
  getVesselNominationFormSchema,
  type VesselNominationFormData,
} from "../schema/vesselNominationFormSchema";
import {
  defaultValues,
  useVesselNominationFormStore,
} from "../store/useVesselNominationForm.store";
import { DOCUMENT_TYPES } from "./documents/Documents";
import VesselDetails from "./header/VesselDetails";

const LANG_BASE = "vessel-nomination";
const TEST_ID_BASE = "add-vessel-nomination-container";
const SERVICE = "bff-vessel";

type VNFormData = VesselNominationFormData & FieldValues;

const AddVesselNominationContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const { setField, setAll, resetForm } = useVesselNominationFormStore();
  const locationState = location.state as
    | {
        vessel?: {
          id?: string | number;
        };
        vesselNominationDetails?: VesselNominationDetailsResponse;
      }
    | undefined;

  const storeSnapshot = useVesselNominationFormStore.getState();
  const formDefaults: VesselNominationFormData = storeSnapshot;

  const schema = useMemo(() => getVesselNominationFormSchema(t), [t]);
  const draftSchema = useMemo(() => getVesselNominationFormSchema(t, true), [t]);

  const form = usePersistentForm<VesselNominationFormData>({
    schema,
    defaultValues: formDefaults,
    storeValues: formDefaults,
    setStoreValues: setAll,
    resetStore: resetForm,
    persistKey: "vessel-nomination-form",
    dateKeys: [FORM_FIELDS.RTA, FORM_FIELDS.ETA, ...nominationDocumentDateKeys],
  });

  const vesselId = form.watch(FORM_FIELDS.VESSEL_ID);
  const selectedVesselId = locationState?.vessel?.id;
  const shouldRedirect = !vesselId && !selectedVesselId;

  const existingNominationDetails = locationState?.vesselNominationDetails;
  const isEditMode = !!existingNominationDetails;

  const userActionPermissions = useAuthStore((state) => state.userActionPermissions);
  const canAddVesselNomination = checkPermission(
    userActionPermissions,
    "canRequestFottVesselNomination",
  );

  const shouldShowSaveDraft =
    canAddVesselNomination &&
    (!isEditMode || locationState?.vesselNominationDetails?.status === "DRAFT") &&
    hasFormData(form.getValues(), defaultValues, [
      FORM_FIELDS.CALL_SIGN,
      FORM_FIELDS.VESSEL_ID,
      FORM_FIELDS.LOA,
      FORM_FIELDS.NET_TONNAGE,
      FORM_FIELDS.GROSS_TONNAGE,
    ]);

  useEffect(() => {
    if (!isEditMode) return;

    return () => {
      clearForm();
    };
  }, [isEditMode]);

  useEffect(() => {
    if (shouldRedirect) {
      navigate(`/${paths.vesselNomination.index}`, { replace: true });
    }
  }, [shouldRedirect, navigate]);

  useEffect(() => {
    if (shouldRedirect) {
      navigate(`/${paths.vesselNomination.index}`, { replace: true });
    }
  }, [shouldRedirect, navigate]);

  if (shouldRedirect) {
    return null;
  }

  useEffect(() => {
    if (shouldRedirect) {
      navigate(`/${paths.vesselNomination.index}`, { replace: true });
    }
  }, [shouldRedirect, navigate]);

  if (shouldRedirect) {
    return null;
  }

  useEffect(() => {
    const selectedVesselId = locationState?.vessel?.id;
    if (!selectedVesselId && !vesselId) {
      navigate(`/${paths.vesselNomination.index}`, { replace: true });
    }
  }, [locationState, vesselId, navigate]);

  const { data: vesselDetails } = useQuery<Vessel>({
    queryKey: ["vessel", SERVICE, vesselId],
    queryFn: () => getVesselByIdNoCompanyIdValidation(SERVICE, vesselId as string),
    enabled: !!vesselId,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (vesselDetails) {
      form.setValue(FORM_FIELDS.LOA, vesselDetails.loa);
      form.setValue(FORM_FIELDS.VESSEL_ID, vesselDetails.id);
      form.setValue(FORM_FIELDS.CALL_SIGN, vesselDetails.vesselCallSign!);
      form.setValue(FORM_FIELDS.NET_TONNAGE, vesselDetails.nrt);
      form.setValue(FORM_FIELDS.GROSS_TONNAGE, vesselDetails.grt);
    }
  }, [vesselDetails]);

  const updateFormData: VesselNominationOutletContext["updateFormData"] = (key, value) => {
    setField(
      key as keyof VesselNominationFormData,
      value as VesselNominationFormData[keyof VesselNominationFormData],
    );
  };

  const [touchedSteps, setTouchedSteps] = useState<Record<number, boolean>>(
    Object.keys(STEP_FIELDS).reduce(
      (acc, key) => {
        acc[Number(key)] = false;
        return acc;
      },
      {} as Record<number, boolean>,
    ),
  );

  const stepperTabsRef = useRef<StepperTabsHandle>(null);
  const currentStepIndex = steps.findIndex((step) => location.pathname.endsWith(step.path));
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const { isLoading } = useUploadDocument();

  const clearStoredFiles = () => {
    const uploadStore = useUploadStore.getState();
    uploadStore.clearFilesForKey(FORM_FIELDS.PAQ_DOCUMENT);
    Object.values(DOCUMENT_TYPES).forEach((documentType) => {
      uploadStore.clearFilesForKey(`vessel-nomination-${documentType}`);
    });
    uploadStore.clearFilesForKey(FORM_FIELDS.ADDITIONAL_NOMINATION_DOCUMENTS);
  };

  const clearForm = useCallback(() => {
    clearStoredFiles();
    resetForm();
    form.reset();
  }, [clearStoredFiles, resetForm, form]);

  const { onSubmit, onSave, isSubmitPending, isSavePending } = useVesselNomination({
    formValues: form.getValues(),
    schema,
    resetForm: clearForm,
    nominationId: locationState?.vesselNominationDetails?.id,
  });

  const onValid = async () => {
    if (isLoading) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }
    await onSubmit();
  };

  const notifyInvalid = (isDraft = false) => {
    if (isDraft) {
      notify({
        title: t(`${LANG_BASE}.notifications.vessel-nomination-draft-saved-error-title`),
        description: t(
          `${LANG_BASE}.notifications.vessel-nomination-draft-saved-error-description`,
        ),
        type: "error",
      });
      return;
    }
    notify({
      title: t(`${LANG_BASE}.notifications.vessel-nomination-submitted-error-title`),
      description: t(`${LANG_BASE}.notifications.vessel-nomination-submitted-error-description`),
      type: "error",
    });
  };

  const onInvalid: SubmitErrorHandler<VesselNominationFormData> = () => {
    notifyInvalid();
  };

  const cancel = () => {
    clearForm();
    navigate(`/${paths.vesselNomination.index}`);
  };

  const complete = () => {
    setTouchedSteps(
      Object.keys(STEP_FIELDS).reduce(
        (acc, key) => {
          acc[Number(key)] = true;
          return acc;
        },
        {} as Record<number, boolean>,
      ),
    );
    form.handleSubmit(onValid, onInvalid)();
  };
  const handleOnStepperTabChange = useCallback(
    async (index: number) => {
      setTouchedSteps((prev) => ({ ...prev, [currentStepIndex]: true }));

      const path = steps[index].path;
      navigate(path, { state: location.state });
    },
    [currentStepIndex, location, navigate],
  );

  const goToNextStep = () => {
    const nextStep = steps[currentStepIndex + 1];
    if (nextStep) {
      stepperTabsRef.current?.next();
    }
  };

  const goToPreviousStep = () => {
    const prevStep = steps[currentStepIndex - 1];
    if (prevStep) {
      stepperTabsRef.current?.back();
    }
  };

  const { statusFor } = useStepStatuses<VNFormData>({
    schema,
    form,
    stepFields: STEP_FIELDS,
    touchedSteps,
    currentStepIndex,
  });

  if (shouldRedirect) {
    return null;
  }

  const getStatusForStep = (index: number): StepStatusValue => {
    const currentStepIndex = stepperTabsRef.current?.getActiveStepperTab();
    if (currentStepIndex === index) return StepStatus.CURRENT;
    return statusFor(index);
  };

  const setFormError = form.setError as unknown as (
    name: string,
    error: { type: "manual"; message: string },
  ) => void;

  const applyZodErrors = (issues: { path: (string | number)[]; message: string }[]) => {
    for (const issue of issues) {
      if (!issue.path.length) continue;
      const name = issue.path.join(".");
      setFormError(name, { type: "manual", message: issue.message });
    }
  };

  const handleSave = async () => {
    form.clearErrors();
    const values = form.getValues();
    const result = await draftSchema.safeParseAsync(values);

    if (!result.success) {
      applyZodErrors(result.error.issues);
      notifyInvalid(true);
      return;
    }

    if (isLoading) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    await onSave();
  };

  const outletCtx: VesselNominationOutletContext = {
    formData: form,
    updateFormData: updateFormData,
    clearErrors: form.clearErrors,
    watch: form.watch,
    setError: form.setError,
  };

  return (
    <div data-testid={TEST_ID_BASE}>
      <div className="pt-8 pb-8 px-24">
        <div className="mb-6">
          <VesselDetails testId={TEST_ID_BASE} vesselDetails={vesselDetails} />
        </div>
        <StepperTabs
          ref={stepperTabsRef}
          testId="form-stepper-tabs"
          onStepperTabChange={handleOnStepperTabChange}
          currentStepIndex={currentStepIndex}
          stepperType={STEPPER_TYPE.VERTICAL}
          isTypeChangeable={false}
        >
          {steps.map((step, index) => (
            <StepperTabPanel
              key={step.path}
              label={t(`${step.label}`)}
              status={getStatusForStep(index)}
            >
              <div className="min-w-auto max-w-full flex flex-col flex-1 bg-white">
                <Outlet context={outletCtx} />
                <div className="pb-6 ">
                  <StepperControls
                    prev={!isFirstStep ? goToPreviousStep : undefined}
                    next={isLastStep ? undefined : goToNextStep}
                    backLabelTranslationKey={
                      steps[currentStepIndex - 1]?.buttonLabel || steps[currentStepIndex - 1]?.label
                    }
                    nextLabelTranslationKey={
                      steps[currentStepIndex + 1]?.buttonLabel || steps[currentStepIndex + 1]?.label
                    }
                  />
                </div>
              </div>
            </StepperTabPanel>
          ))}
        </StepperTabs>
        <div className="py-6 border-t border-t-[var(--stroke-secondary-stroke,#E6EDF2)]">
          <StepperControls
            cancel={cancel}
            complete={complete}
            save={shouldShowSaveDraft ? handleSave : undefined}
            savePending={isSavePending}
            completePending={isSubmitPending}
            completeLabelTranslationKey="stepper-controls.submit"
          />
        </div>
      </div>
    </div>
  );
};

export default AddVesselNominationContainer;
